import { normalizeAssetKeyQuery } from '@/features/assets/lib/assetKeyQuery';
import type { AssetSort } from '@/features/assets/types/asset';
import { useAuthStore } from '@/features/authentication/stores/authStore';
import type { DeliveryFilterOperator } from '@/features/delivery/types/contracts';
import { fetchProjectAssets } from '@/features/raw-footage/api/projects';
import {
  PROJECT_ASSETS_PAGE_SIZE,
  RAW_FOOTAGE_KEY,
  RAW_FOOTAGE_STALE_TIME,
} from '@/features/raw-footage/config/constants';
import { infiniteQueryOptions, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import type { InventoryFilters } from '../types/inventory';

export interface ProjectAssetsQueryOptions extends InventoryFilters {
  enabled?: boolean;
  q?: string | null;
  sort?: AssetSort;
  rejected?: boolean;
  fieldLogLinked?: boolean;
  includeFieldLog?: boolean;
  delivery?: string;
  deliveryOperator?: DeliveryFilterOperator;
}

/**
 * `queryOptions` factory for a project's assets, optionally filtered and ordered.
 * Each complete sort value owns an independent infinite-query cursor chain.
 */
export function projectAssetsQuery(
  projectId: string,
  {
    q,
    sort = null,
    rejected,
    delivery = 'all',
    deliveryOperator = 'in',
    enabled = true,
    mediaType,
    source,
    minDurationMs,
    maxDurationMs,
    fieldLogLinked,
    includeFieldLog = false,
  }: ProjectAssetsQueryOptions = {},
) {
  const normalizedQ = normalizeAssetKeyQuery(q);
  return infiniteQueryOptions({
    queryKey: [
      RAW_FOOTAGE_KEY,
      'project',
      projectId,
      'assets',
      {
        q: normalizedQ ?? undefined,
        sort,
        rejected,
        delivery,
        deliveryOperator,
        mediaType,
        source,
        minDurationMs,
        maxDurationMs,
        fieldLogLinked,
        includeFieldLog,
        principal: useAuthStore.getState().user?.uid ?? null,
      },
    ],
    queryFn: ({ pageParam, signal }) =>
      fetchProjectAssets(
        projectId,
        {
          limit: PROJECT_ASSETS_PAGE_SIZE,
          cursor: pageParam,
          q: normalizedQ,
          sort,
          rejected,
          delivery,
          deliveryOperator,
          mediaType,
          source,
          minDurationMs,
          maxDurationMs,
          fieldLogLinked,
          includeFieldLog,
        },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!projectId && enabled,
    staleTime: RAW_FOOTAGE_STALE_TIME,
    refetchOnWindowFocus: true,
    retry: false,
    // The key varies with the search term and the sort, so either change must narrow or reorder the
    // list in place instead of emptying it; the route's pendingComponent only covers the cold load.
    placeholderData: keepPreviousData,
    // The route loader owns the cold unfiltered load. Observer errors stay local so search/sort
    // failures cannot replace the retained project rows.
    throwOnError: false,
  });
}

export function useProjectAssets(projectId: string, options: ProjectAssetsQueryOptions = {}) {
  return useInfiniteQuery(projectAssetsQuery(projectId, options));
}
